import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Divider,
  Grid,
  Chip,
  Stack,
  Tooltip,
} from '@mui/material';
import { PortfolioBot } from 'types/ApiServicesTypes';
import { getColorByNum, formatNumber } from 'utils/GenericFunctions';
import type { UseGetPortfolioRes } from 'services/Portfolio/useGetPortfolioActivePaper';
import MuiChip from 'app/design/uiComponents/MuiChip';
import BarChartIcon from '@mui/icons-material/BarChart';
import { styled } from '@mui/material/styles';

import PortfolioTable from 'app/design/speedBot/ComponentItem/Portfolio/PortfolioTable';
import PortfolioStatsCard from './PortfolioStatsCard';
import IndividualOnboarding from 'app/design/speedBot/ComponentItem/Portfolio/IndividualOnboarding';

interface BrokerwisePositionsCardsProps {
  portfolioData?: UseGetPortfolioRes;
  liveFeedTodaysPosData: { [id: number]: number };
}

type BrokerGroup = {
  broker_name: string;
  bots: PortfolioBot[];
};

function groupByBroker(bots: PortfolioBot[]): BrokerGroup[] {
  const groups: { [broker: string]: PortfolioBot[] } = {};
  bots.forEach(bot => {
    const broker = bot.broker_name || 'Unknown Broker';
    if (!groups[broker]) groups[broker] = [];
    groups[broker].push(bot);
  });
  return Object.entries(groups).map(([broker_name, bots]) => ({
    broker_name,
    bots,
  }));
}

// Styled box for value display (like Max Profit/Max Loss)
const StatBox = styled(Box)(({ theme }) => ({
  flex: 1,
  background: theme.palette.grey[50],
  borderRadius: 10,
  padding: theme.spacing(1.5, 1),
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  minWidth: 100,
  boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
}));

const BrokerwisePositionsCards: React.FC<BrokerwisePositionsCardsProps> = ({
  portfolioData,
  liveFeedTodaysPosData,
}) => {
  const [selectedGroup, setSelectedGroup] = useState<string | null>(null);
  // Extract only the 'active' bots from the provided portfolioData
  const activeBots: PortfolioBot[] = portfolioData?.active?.bots || [];
  if (!activeBots || activeBots.length === 0) {
    return <IndividualOnboarding type="active" />;
  }
  const brokerGroups = groupByBroker(activeBots);

  // Get status counts for total bots

  const totalInitialCapital = activeBots.reduce(
    (sum, bot) => sum + (bot.initial_capital || 0),
    0,
  );
  const totalPortfolioValue = activeBots.reduce(
    (sum, bot) => sum + (bot.portfolio_value || 0),
    0,
  );
  const totalProfitLoss = activeBots.reduce(
    (sum, bot) => sum + (bot.bot_info?.total_profit_loss || 0),
    0,
  );
  const orderCount = activeBots.reduce(
    (sum, bot) => sum + (bot.bot_info?.order_count || 0),
    0,
  );
  const totalNetProfit = activeBots.reduce(
    (sum, bot) => sum + (bot.bot_info?.net_profit || 0),
    0,
  );
  const totalFees = activeBots.reduce(
    (sum, bot) => sum + (bot.bot_info?.total_fees || 0),
    0,
  );
  const totalOpenPositions = activeBots.reduce(
    (sum, bot) => sum + (bot.open_position_count || 0),
    0,
  );
  const totalPositions = activeBots.reduce(
    (sum, bot) => sum + (bot.position_count || 0),
    0,
  );
  const totalTodaysChange = activeBots.reduce(
    (sum, bot) =>
      sum +
      (liveFeedTodaysPosData[bot.id] !== undefined
        ? liveFeedTodaysPosData[bot.id]
        : bot.todays_change || 0),
    0,
  );

  return (
    <Box p={2}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: 3,
          overflowX: 'auto',
          pb: 2,
          '&::-webkit-scrollbar': { height: 8 },
          '&::-webkit-scrollbar-thumb': { background: '#ccc', borderRadius: 4 },
        }}
      >
        {/* Total Bots Card */}
        <PortfolioStatsCard
          title="Total Bots"
          botCount={activeBots.length}
          positions={{ open: totalOpenPositions, total: totalPositions }}
          todaysChange={totalTodaysChange}
          currencySymbol={activeBots[0]?.currency_symbol || ''}
          portfolioValue={totalPortfolioValue}
          totalPL={totalProfitLoss}
          initialCapital={totalInitialCapital}
          netProfit={totalNetProfit}
          bots={activeBots}
          selected={selectedGroup === null}
          onClick={() => setSelectedGroup(null)}
        />
        {/* Brokerwise Cards */}
        {brokerGroups.map(group => {
          // Calculate totals for this broker
          const totalInitialCapital = group.bots.reduce(
            (sum, bot) => sum + (bot.initial_capital || 0),
            0,
          );
          const totalPortfolioValue = group.bots.reduce(
            (sum, bot) => sum + (bot.portfolio_value || 0),
            0,
          );
          const totalProfitLoss = group.bots.reduce(
            (sum, bot) => sum + (bot.bot_info?.total_profit_loss || 0),
            0,
          );
          const orderCount = group.bots.reduce(
            (sum, bot) => sum + (bot.bot_info?.order_count || 0),
            0,
          );
          const totalNetProfit = group.bots.reduce(
            (sum, bot) => sum + (bot.bot_info?.net_profit || 0),
            0,
          );
          const totalFees = group.bots.reduce(
            (sum, bot) => sum + (bot.bot_info?.total_fees || 0),
            0,
          );
          const totalOpenPositions = group.bots.reduce(
            (sum, bot) => sum + (bot.open_position_count || 0),
            0,
          );
          const totalPositions = group.bots.reduce(
            (sum, bot) => sum + (bot.position_count || 0),
            0,
          );
          const totalTodaysChange = group.bots.reduce(
            (sum, bot) =>
              sum +
              (liveFeedTodaysPosData[bot.id] !== undefined
                ? liveFeedTodaysPosData[bot.id]
                : bot.todays_change || 0),
            0,
          );

          return (
            <PortfolioStatsCard
              key={group.broker_name}
              title={group.broker_name}
              botCount={group.bots.length}
              positions={{ open: totalOpenPositions, total: totalPositions }}
              todaysChange={totalTodaysChange}
              currencySymbol={group.bots[0]?.currency_symbol || ''}
              portfolioValue={totalPortfolioValue}
              totalPL={totalProfitLoss}
              initialCapital={totalInitialCapital}
              netProfit={totalNetProfit}
              bots={group.bots}
              selected={selectedGroup === group.broker_name}
              onClick={() => setSelectedGroup(group.broker_name)}
            />
          );
        })}
      </Box>
      {/* Table below cards */}
      <Box mt={4}>
        {selectedGroup === null ? (
          <PortfolioTable
            bots={activeBots}
            ptype={portfolioData?.active ? 'active' : 'paper'}
            liveFeedTodaysPosData={liveFeedTodaysPosData}
          />
        ) : (
          brokerGroups
            .filter(group => group.broker_name === selectedGroup)
            .map(group => (
              <PortfolioTable
                key={group.broker_name}
                bots={group.bots}
                ptype={portfolioData?.active ? 'active' : 'paper'}
                liveFeedTodaysPosData={liveFeedTodaysPosData}
              />
            ))
        )}
      </Box>
    </Box>
  );
};

export default BrokerwisePositionsCards;
